import { useEffect } from 'react';
import { X, Loader2, Gift, Sparkles, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Reusable informational modal used by the deposit flow.
 *
 * Variants:
 *  - "pending"   — "Please wait, explore rewards while we credit your wallet"
 *  - "credited"  — "Wallet loaded! Enjoy the game, happy winning!"
 *  - "rejected"  — "Your deposit was declined — reach out to support"
 */
export default function WalletNotificationModal({
  open,
  variant = 'pending',
  amount = null,
  reason = null,
  onClose = () => {},
  onGoToRewards = null,
  autoDismissMs = 8000,
}) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(t);
  }, [open, autoDismissMs, onClose]);

  if (!open) return null;

  const isPending = variant === 'pending';
  const isRejected = variant === 'rejected';
  const accent = isRejected ? '#ef4444' : (isPending ? '#F5D76E' : '#22c55e');
  const formattedAmount = amount == null
    ? ''
    : Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid={`wallet-notification-${variant}`}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border bg-[#131822] p-7 shadow-2xl animate-in fade-in zoom-in duration-300"
        style={{ borderColor: `${accent}44` }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-white/5 hover:text-white transition"
          data-testid="wallet-notification-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
            style={{ background: `${accent}1F`, color: accent }}
          >
            {isPending ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : isRejected ? (
              <XCircle className="w-9 h-9" />
            ) : (
              <CheckCircle2 className="w-9 h-9" />
            )}
          </div>

          <h3 className="text-2xl font-bold text-white mb-2">
            {isPending && 'Please wait a moment…'}
            {isRejected && 'Deposit declined'}
            {!isPending && !isRejected && 'Your wallet is loaded!'}
          </h3>

          {isPending && (
            <>
              <p className="text-sm text-gray-300 leading-relaxed">
                Your deposit is being reviewed. Explore our rewards while we
                credit your wallet — you'll be notified as soon as it lands.
              </p>
              <p className="text-xs text-gray-500 mt-2 italic">
                Thank you for your patience 🎰
              </p>
              {onGoToRewards && (
                <button
                  onClick={() => { onGoToRewards(); onClose(); }}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold"
                  style={{ background: accent, color: '#0B0E14' }}
                  data-testid="wallet-notification-explore-rewards"
                >
                  <Gift className="w-4 h-4" /> Explore Rewards
                </button>
              )}
            </>
          )}

          {isRejected && (
            <>
              <p className="text-sm text-gray-300 leading-relaxed">
                {reason || 'Your deposit request was declined.'}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Please reach out to support if you have questions.
              </p>
            </>
          )}

          {!isPending && !isRejected && (
            <>
              {amount != null && (
                <p className="text-3xl font-extrabold mb-2" style={{ color: accent }}>
                  +${formattedAmount}
                </p>
              )}
              <p className="text-sm text-gray-300 leading-relaxed">
                Please enjoy your game. Happy winning!
              </p>
              <p className="text-xs text-[#F5D76E]/80 mt-3 flex items-center justify-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Good luck!
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
